import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {HeaderService} from '../../../services/header-service';
import {TextsComponent} from '../../elements/texts/texts.component';
import {ButtonComponent} from '../../elements/button/button.component';
import {Location, NgIf} from '@angular/common';
import {IconComponent} from '../../elements/icon/icon.component';
import {Icons} from '../../../model/icon';
import {HotelDtoService} from '../../../services/hotel-dto-service';
import {LocationDto} from '../../../model/continent';
import {environment} from '../../../environments/environment';
import {Router, RouterLink} from '@angular/router';
import {ArticleService} from '../../../services/article.service';
import {Article} from '../../../model/article';
import {getLastSegment} from '../../../functions/getLastSegment';
import {ThemeService} from '../../../services/theme.service';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {NavigationService} from '../../../services/navigation-service';
import {FeedbackService} from '../../../services/feedback-service';
import {CountryService} from '../../../services/country-service';
import {PaginationParams} from '../../../model/pagination';
import {Subscription, timeout} from 'rxjs';
import {LoadingService} from '../../../services/loading-service';

@Component({
  selector: 'app-hotel-page',
  imports: [
    TextsComponent,
    ButtonComponent,
    NgIf,
    IconComponent,
    RouterLink,
  ],
  templateUrl: './hotel-page.component.html',
  standalone: true,
  styleUrl: './hotel-page.component.scss',
  animations: [
    trigger('expandAnimation', [
      state('void', style({
        transform: 'scale(0.1)',
        opacity: 0
      })),
      state('*', style({
        transform: 'scale(1)',
        opacity: 1
      })),
      transition('void => *', [
        animate('0.3s ease-out')
      ]),
      transition('* => void', [
        animate('0.2s ease-in')
      ])
    ])
  ]
})
export class HotelPageComponent implements OnInit, OnDestroy {
  constructor(protected interfaceService: HeaderService,
              private hotelDtoService: HotelDtoService,
              private router: Router,
              public themeService: ThemeService,
              public location: Location,
              public navigationService: NavigationService,
              public feedValue: FeedbackService,
              private countryService: CountryService,
              public loadingService: LoadingService,
              private articleService: ArticleService,) {
  }

  hotelArticle?: Article;
  articleLoading = true;
  loadError = false;

  @Input() url?: string;
  @Input() preview?: boolean;
  @Output() clickOn = new EventEmitter();

  openWindow: boolean = false;
  tab: boolean = false;

  hotel: LocationDto = new LocationDto();
  countries: LocationDto[] = [];

  background: string = '';
  backgroundLoaded: boolean = false;
  backgroundError: boolean = false;
  imageUrl: string = '';

  loading: boolean = false;
  imageLoading: boolean = false;
  dataLoaded: boolean = false;
  imageLoadComplete: boolean = false;

  defaultParams = new PaginationParams({
    sort: 'titleEn:ASC',
    query: '',
    pageSize: 1000,
    pageNumber: 0,
  });

  private countriesSubscription?: Subscription;
  private hotelSubscription?: Subscription;
  private destroyed = false;
  private countryNameVariants: Map<number, string[]> = new Map();

  ngOnInit() {
    this.loading = true;
    this.loadingService.setLoadingValue(true);
    this.interfaceService.setPageInterface('header-transparent');
    this.interfaceService.setThisHotel(true);
    const url = this.url ?? getLastSegment(this.router.parseUrl(this.router.url));
    // Country names enrich the address but are not a dependency of the hotel request.
    this.countriesSubscription = this.countryService.getCountryList(this.defaultParams).pipe(timeout(12000)).subscribe({
      next: countries => {
        this.countries = countries.content ?? [];
        this.prepareCountryVariants();
      },
      error: () => {
        this.countries = [];
      }
    });
    this.loadHotel(url);

    if (window.innerWidth < 769) {
      this.tab = true;
    }
  }

  retryHotel() {
    const url = this.url ?? getLastSegment(this.router.parseUrl(this.router.url));
    this.loadHotel(url);
  }

  private loadHotel(url: string) {
    this.hotelSubscription?.unsubscribe();
    this.loadError = false;
    this.loading = true;
    this.articleLoading = true;
    this.hotelArticle = undefined;
    this.loadingService.setLoadingValue(true);
    this.hotelSubscription = this.hotelDtoService.getHotelByUrl(url).pipe(timeout(12000)).subscribe({
      next: (data) => {
        this.hotel = new LocationDto(data);
        this.articleService.forHotel(this.hotel).then(articles => {
          if (!this.destroyed) this.hotelArticle = articles[0];
        }).catch(() => this.hotelArticle = undefined).finally(() => this.articleLoading = false);
        this.dataLoaded = true;

        if (this.location.path() !== '/admin') {
          if (this.hotel.displayPage === false || this.hotel.url === '') {
            this.navigationService.backOrHome();
            this.loadingService.setLoadingValue(false);
            return;
          }
        }
        this.loadBackgroundImage();
        this.checkLoadingComplete();
      }, error: () => {
        this.loadError = true;
        this.loading = false;
        this.articleLoading = false;
        this.loadingService.setLoadingValue(false);
      }
    });
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.hotelSubscription?.unsubscribe();
    this.loadingService.setLoadingValue(false);
    this.interfaceService.setHotelOpenGeo(false);
    this.interfaceService.setThisHotel(false);
    if (this.countriesSubscription) {
      this.countriesSubscription.unsubscribe();
    }
  }

  private loadBackgroundImage(): void {
    if (!this.hotel.image) {
      this.backgroundLoaded = true;
      this.imageLoadComplete = true;
      this.setBackground();
      this.checkLoadingComplete();
      return;
    }
    this.imageLoading = true;
    const path = environment.s3Prefix + this.hotel.image;
    this.imageUrl = path;
    const img = new Image();
    img.onload = () => {
      if (this.destroyed) return;
      this.backgroundLoaded = true;
      this.imageLoading = false;
      this.imageLoadComplete = true;
      this.setBackground();
      this.checkLoadingComplete();
    };
    img.onerror = () => {
      if (this.destroyed) return;
      console.error('Failed to load background image:', path);
      this.backgroundError = true;
      this.imageLoading = false;
      this.imageLoadComplete = true;
      this.setBackground();
      this.checkLoadingComplete();
    };
    img.src = path;
    if (img.complete) {
      this.backgroundLoaded = true;
      this.imageLoading = false;
      this.imageLoadComplete = true;
      this.setBackground();
      this.checkLoadingComplete();
    }
  }

  private checkLoadingComplete(): void {
    if (this.dataLoaded) {
      this.loading = false;
      this.loadingService.setLoadingValue(false);
    }
  }

  setBackground() {
    const pre = this.preview ? 'position: absolute;' : '';
    let backgroundStyle = '';
    if (this.backgroundLoaded && this.imageUrl) {
      const filter = this.hotel.cssStyleImage ? this.hotel.cssStyleImage : '';
      backgroundStyle = `background-image: url('${this.imageUrl}'); ${pre} ${filter}`;
    } else {
      backgroundStyle = `background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); ${pre}`;
    }
    this.background = backgroundStyle;
  }

  get backgroundClasses(): string {
    const classes = [];
    if (this.imageLoading) {
      classes.push('image-loading');
    }
    if (this.backgroundLoaded) {
      classes.push('image-loaded');
    }
    return classes.join(' ');
  }

  get formattedAddress(): string {
    if (this.hotel.address) {
      return this.formatShortAddress(this.hotel.address);
    }
    return '';
  }

  private prepareCountryVariants(): void {
    this.countryNameVariants.clear();
    this.countries.forEach(country => {
      if (country.id) {
        const variants: string[] = [];
        if (country.titleEn) {
          variants.push(country.titleEn.toLowerCase());
          const enLower = country.titleEn.toLowerCase();
          if (enLower.includes('united states')) {
            variants.push('usa', 'u.s.a', 'u.s.a.', 'us', 'america', 'united states of america');
          } else if (enLower.includes('united kingdom')) {
            variants.push('uk', 'u.k.', 'great britain', 'england', 'britain');
          } else if (enLower.includes('russian federation')) {
            variants.push('russia');
          }
        }
        if (country.titleRu) {
          variants.push(country.titleRu.toLowerCase());
        }
        this.countryNameVariants.set(country.id, variants);
      }
    });
  }

  emitClick() {
    this.feedValue.setFeedbackValue([{text: ['Бронирование отелей', 'Hotel Booking']}]);
    this.router.navigate(['/feedback']).then((r) => r);
  }

  getInvert() {
    return this.interfaceService.getActiveInterface() != 'header-transparent';
  }

  formatShortAddress(fullAddress: string): string {
    if (!fullAddress || fullAddress.trim() === '') {
      return '';
    }
    const cleanedAddress = fullAddress.trim();
    const parts = cleanedAddress.split(',').map(part => part.trim());
    if (parts.length === 0) {
      return cleanedAddress;
    }
    let name = parts[0];
    name = this.cleanName(name);
    let country = this.extractCountryFromParts(parts);
    if (!country && parts.length > 1) {
      country = parts[parts.length - 1];
    }
    country = this.formatCountryName(country);
    return country ? `${name}, ${country}` : name;
  }

  private cleanName(name: string): string {
    const patterns = [
      /^\d+\s+/,
      /\s+\d+$/,
      /^(strada|via|calle|c\.|ул\.|улица|проспект|пр\.|бульвар|б-р)\s+/i,
      /^(avenue|av\.|street|st\.|road|rd\.|boulevard|blvd\.)/i,
    ];
    let cleanedName = name;
    for (const pattern of patterns) {
      cleanedName = cleanedName.replace(pattern, '');
    }
    cleanedName = cleanedName.trim().replace(/^,|,$/g, '');
    return cleanedName || name;
  }

  private extractCountryFromParts(parts: string[]): string | null {
    if (parts.length === 0 || this.countries.length === 0) {
      return null;
    }
    for (let i = parts.length - 1; i >= 0; i--) {
      const part = parts[i].toLowerCase();
      for (const country of this.countries) {
        if (country.id && this.countryNameVariants.has(country.id)) {
          const variants = this.countryNameVariants.get(country.id)!;
          for (const variant of variants) {
            if (part === variant || part.includes(variant)) {
              return this.getPreferredCountryName(country);
            }
          }
        }
      }
    }
    return null;
  }

  private getPreferredCountryName(country: LocationDto): string {
    return country.titleEn || country.titleRu || '';
  }

  private formatCountryName(country: string | null): string {
    if (!country) {
      return '';
    }
    const lowerCountry = country.toLowerCase().trim();
    for (const countryObj of this.countries) {
      const variants = countryObj.id ? this.countryNameVariants.get(countryObj.id) : [];
      if (variants?.some(variant => lowerCountry.includes(variant) || variant.includes(lowerCountry))) {
        return this.formatCountryDisplayName(countryObj);
      }
    }
    return this.capitalizeFirstLetter(country);
  }

  private formatCountryDisplayName(country: LocationDto): string {
    if (country.titleEn) {
      return this.capitalizeProperly(country.titleEn);
    }
    if (country.titleRu) {
      return country.titleRu.charAt(0).toUpperCase() + country.titleRu.slice(1);
    }
    return '';
  }

  private capitalizeProperly(text: string): string {
    if (!text) return '';
    const exceptions = ['and', 'or', 'the', 'of', 'in', 'on', 'at', 'by', 'to'];
    return text.toLowerCase()
      .split(' ')
      .map((word, index) => {
        if (index === 0 || !exceptions.includes(word)) {
          return word.charAt(0).toUpperCase() + word.slice(1);
        }
        return word;
      })
      .join(' ');
  }

  get hotelText() {
    const cityEn = this.hotel.city?.titleEn.replaceAll('-city', '') ?? '';
    const cityRu = this.hotel.city?.titleRu.replaceAll('-city', '') ?? '';
    const countryEn = this.hotel.city?.country?.titleEn.replaceAll('-city', '') ?? '';
    const countryRus = this.hotel.city?.country?.titleRu.replaceAll('-city', '') ?? '';
    return [
      {text: [`${cityRu}, ${countryRus}`, `${cityEn}, ${countryEn}`],},
    ];
  }

  goBack() {
    this.location.back();
  }

  private capitalizeFirstLetter(text: string): string {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }

  protected readonly Icons = Icons;
}
